/**
 * Alert component
 * Displays messages with different severity levels
 */

import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle, Info, XCircle, X } from 'lucide-react';
import { useState } from 'react';

type AlertType = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  type: AlertType;
  message: string;
  title?: string;
  className?: string;
  dismissible?: boolean;
}

const alertStyles: Record<
  AlertType,
  { bg: string; text: string; border: string; icon: React.ReactNode }
> = {
  success: {
    bg: 'bg-green-50',
    text: 'text-green-800',
    border: 'border-green-200',
    icon: <CheckCircle className="h-5 w-5 text-green-500" />,
  },
  error: {
    bg: 'bg-red-50',
    text: 'text-red-800',
    border: 'border-red-200',
    icon: <XCircle className="h-5 w-5 text-red-500" />,
  },
  warning: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
    icon: <AlertCircle className="h-5 w-5 text-yellow-500" />,
  },
  info: {
    bg: 'bg-blue-50',
    text: 'text-blue-800',
    border: 'border-blue-200',
    icon: <Info className="h-5 w-5 text-blue-500" />,
  },
};

export function Alert({
  type,
  message,
  title,
  className,
  dismissible = false,
}: AlertProps) {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const styles = alertStyles[type];

  return (
    <div
      className={cn(
        'rounded-lg border p-4 shadow-sm backdrop-blur-sm',
        styles.bg,
        styles.text,
        styles.border,
        className
      )}
      role="alert"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">{styles.icon}</div>
        <div className="ml-3 flex-1">
          {title && <h3 className="text-sm font-semibold">{title}</h3>}
          <p className="text-sm">{message}</p>
        </div>
        {dismissible && (
          <button
            type="button"
            className={cn(
              '-mx-1.5 -my-1.5 ml-auto rounded-lg p-1.5',
              styles.text,
              `hover:bg-${type}-100 focus:ring-2 focus:ring-${type}-400 focus:ring-offset-2`
            )}
            onClick={() => setIsVisible(false)}
          >
            <span className="sr-only">Dismiss</span>
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
